import { inject, Injectable, signal } from "@angular/core";
import { readContracts, simulateContract, waitForTransactionReceipt, writeContract, WriteContractErrorType } from "@wagmi/core";
import { Address, formatEther, parseEther, parseUnits, WriteContractReturnType } from "viem";
import { TranslateService } from "@ngx-translate/core";

import { environment } from '../../../environments/environment';
import { SwapStatus, getParam } from '../../shared/constants/app-enums';
import { getChainData, getPresaleContract, getUSDCContract, getUSDTContract, presaleContractEth, stakeContract, token } from '../../shared/constants/wallet-connect.const';
import { AnalyticsService } from '../analytics.service';
import { PresaleDataService } from '../connection-services/data.service';
import { PopupService } from '../popup.service';
import { PixelService } from '../pixel.service';
import { getBlock } from '@wagmi/core';

@Injectable({
    providedIn: 'root',
})


export class EvmPurchseService {
    // Single source of truth for the current transaction state — read by swap, staking, wert, and claim components
    private readonly _transactionUpdates = signal<{ status: SwapStatus, hash?: string }>({ hash: "", status: SwapStatus.not_started });
    readonly transactionUpdates = this._transactionUpdates.asReadonly();

    private readonly dataService = inject(PresaleDataService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly popupService = inject(PopupService);
    private readonly transletService = inject(TranslateService);
    private readonly pixelService = inject(PixelService);

    /** Resets transaction state — call from component ngOnDestroy or after a complete/failed flow */
    resetTransactionStatus(): void {
        this._transactionUpdates.set({ hash: "", status: SwapStatus.not_started });
    }

    /** Updates transaction state — used by SolPurchaseService and WertComponent which share this signal */
    setTransactionStatus(status: SwapStatus, hash?: string): void {
        this._transactionUpdates.set({ status, hash });
    }

    /**
     * this function is swaping the native token with app token
     * @param amount: app token amount
     * @param value: eth token value
     */

    async swapNativeTokens(amount: number, value: number, isBuyStake: boolean = false) {
        // setting swapStatus to not started
        this._transactionUpdates.set({ status: SwapStatus.not_started })

        /**
         * preparing a contract for swaping via smart contract
         * method of ethBuyHelper
         */

        // const referralAddress = getParam('referralAddress') || "0x0000000000000000000000000000000000000000";
        // const isValidReferral = await checkIsValidReferral(referralAddress, this.dataService.chainId, this.dataService.walletAddress, this.dataService.wagmiConfig);
        // const refferal = isValidReferral ? referralAddress : "0x0000000000000000000000000000000000000000";


        try {
            // const referral = await this.getValidReferral();
            const { request } = await simulateContract(this.dataService.wagmiConfig, {
                ...getPresaleContract(this.dataService.chainId),
                functionName: getChainData(this.dataService.chainId)?.nativeFunction,
                chainId: getChainData(this.dataService.chainId).chainIdInt,
                args: getChainData(this.dataService.chainId).hasStaking ? [amount, isBuyStake] : [amount],
                // args: this.dataService.chainId === environment.eth.chainIdInt ? [amount, false, referral] : [amount, referral],
                value: parseEther(`${value}`),
                // account:      this.walletAddress as Address    // Optional param. Not required!
            });
            // setting swapStatus to confirmation pending
            this._transactionUpdates.set({ status: SwapStatus.confirm_pending })
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            // Generate unique transaction ID for this buy flow
            const txId = this.pixelService.generateTransactionId();

            // Track buy initiated
            const chainName = this.dataService.chainId === environment.eth.chainIdInt ? 'ETH' : 'BNB';
            const paymentMethod = getChainData(this.dataService.chainId).purchaseToken.toLowerCase();
            this.trackEvent('buy_event', 'initiated', txId, {
                amount_token_expected: amount,
                amount_usd_estimated: +(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2),
                payment_method: paymentMethod,
                type: isBuyStake ? 'buy_and_stake' : 'buy'
            });
            /**
             * Writing contract for swap process
             */
            await writeContract(this.dataService.wagmiConfig, request).then(
                (hash: string) => {
                    this._transactionUpdates.set({ status: SwapStatus.in_progess, hash: hash })
                    this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                    this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.dataService.walletAddress, hash, this.dataService.balanceDataEVM, this.dataService.chainId, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT);
                    // Track buy pending
                    this.trackEvent('buy_event', 'pending', txId, {
                        amount_token_expected: amount,
                        amount_usd_estimated: +(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2),
                        payment_method: paymentMethod,
                        tx_hash: hash,
                        type: isBuyStake ? 'buy_and_stake' : 'buy'
                    });
                    this.checkTransaction(hash, getChainData(this.dataService.chainId).purchaseToken, amount, value, isBuyStake ? 'buy_and_stake' : 'buy', this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bnb', txId);
                },
                (err: any) => {
                    if (err.code === 4001) {
                        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                        this.vwoData('failedAndRejectTransaction', {
                            transactionStatus: 'rejected',
                            fundSpentAmount: value,
                            tokenAmountPurchase: amount,
                            purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                            tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                            transactionId: '',
                            walletAddress: this.dataService.walletAddress,
                            walletType: this.dataService.connectedWalletName,
                        });
                        // Track buy failed (user rejected)
                        this.trackEvent('buy_event', 'failed', txId, {
                            amount_token_expected: amount,
                            amount_usd_estimated: +(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2),
                            payment_method: paymentMethod,
                            failure_type: 'user_rejected',
                            failure_message: err?.message || 'User rejected transaction',
                            type: isBuyStake ? 'buy_and_stake' : 'buy'
                        });
                        this._transactionUpdates.set({ status: SwapStatus.rejected })
                    } else {
                        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                        this.vwoData('failedAndRejectTransaction', {
                            transactionStatus: 'failed',
                            fundSpentAmount: value,
                            tokenAmountPurchase: amount,
                            purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                            tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                            transactionId: '',
                            walletAddress: this.dataService.walletAddress,
                            walletType: this.dataService.connectedWalletName,
                        });
                        // Track buy failed (RPC error)
                        this.trackEvent('buy_event', 'failed', txId, {
                            amount_token_expected: amount,
                            amount_usd_estimated: +(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2),
                            payment_method: paymentMethod,
                            failure_type: 'rpc_error',
                            failure_message: err?.message || 'Transaction failed',
                            type: isBuyStake ? 'buy_and_stake' : 'buy'
                        });
                        this._transactionUpdates.set({ status: SwapStatus.failed })
                    }
                },
            );
        } catch (error: any) {
            this.popupService.messagePopup({ icon: 'error', title: 'Transaction Error', desc: error?.message || error.toString() });
        }
    }

    /**
     * this function is check the final
     * status of the current transaction
     * @param hashKey: transaction hash key
     * @param mode: Eth/Bnb/Poly/Usdt transaction
     * @param amount: no of tooken
     * @param value:  amount of eth/bnb/usdt
     */

    private getBasePixelEventData() {
        return {
            wallet_address: this.dataService.walletAddress,
            wallet_type: 'EVM' as const,
            wallet_name: this.dataService.connectedWalletName,
            token_symbol: environment.tokenName,
            chain: (this.dataService.chainId === environment.eth.chainIdInt ? 'ETH' : 'BNB') as 'ETH' | 'BNB',
            chain_id: this.dataService.chainId,
        };
    }

    private trackEvent(eventType: 'buy_event' | 'stake_event' | 'claim_event' | 'withdraw_event', action: 'initiated' | 'pending' | 'success' | 'failed', txId: string = '', data: any = {}) {
        if (txId && txId != '') {

            this.pixelService.sendPixelEvent(eventType, {
                action,
                tx_id: txId,
                ...this.getBasePixelEventData(),
                ...data
            });
        }
    }

    /**
     * Get validated referral address from URL parameter or return null address
     * Checks if the referral address is valid before returning
     * @returns Valid referral address or null address if invalid
     */
    // private async getValidReferral(): Promise<Address> {
    //     const nullAddress = '0x0000000000000000000000000000000000000000' as Address;
    //     let referralAddress = getParam('referralAddress');
    //     if (!referralAddress) {
    //         return nullAddress;
    //     }
    //     const isValidReferral = await checkIsValidReferral(referralAddress, this.dataService.chainId, this.dataService.walletAddress, this.dataService.wagmiConfig);
    //     return isValidReferral ? (referralAddress as Address) : nullAddress;
    // }

    async checkTransaction(hashKey: string, mode: string, amount: number, value: number, purchaseType: string, crypto: 'eth' | 'eth_usdt' | 'eth_usdc' | 'bnb' | 'bnb_usdt' | 'bnb_usdc' | 'usd' | '', txId: string) {
        await waitForTransactionReceipt(this.dataService.wagmiConfig, { hash: hashKey as Address }).then(
            async (response: { transactionHash: string, blockNumber: bigint }) => {
                clearTimeout(this.dataService.refreshId);
                this.dataService.getPresalesData()
                this._transactionUpdates.set({ status: SwapStatus.complete, hash: response.transactionHash })
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'completed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: response.transactionHash });
                this.vwoData('successfulTransaction', {
                    walletAddress: this.dataService.walletAddress,
                    transactionId: response.transactionHash,
                    mode: mode === 'usd' ? 'WERT' : 'CARYPTO',
                    walletName: this.dataService.connectedWalletName,
                    purchaseType,
                    tokenAmountPurchase: amount,
                    amountSpent: value,
                    cryptoType: crypto,
                    usdAmount: mode === 'usdt' || mode === 'usdc' ? +value : +(+(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2)),
                });
                // this.swapHash = response.transactionHash; // assigning succes transaction hash id
                this.analyticsService.sendBWData(mode, amount, value, this.dataService.walletAddress, response.transactionHash, this.dataService.chainId, mode, this.dataService.balanceDataEVM, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT);
                this.analyticsService.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, mode, environment.tokenName, value, amount, this.dataService.walletAddress, response.transactionHash, this.dataService.balanceDataEVM, this.dataService.chainId, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT);
                // this.analyticsService.bestWalletModal(this.mode);
                // Track success - common for all transaction types if txId is passed
                if (txId) {
                    try {
                        // Fetch block details for accurate timestamp and block number
                        const block = await getBlock(this.dataService.wagmiConfig, { blockNumber: response.blockNumber });
                        const blockNumber = Number(response.blockNumber);
                        const blockTimestamp = Number(block.timestamp) * 1000; // Convert to milliseconds if needed, or keep as seconds. Usually ISO string is preferred or match backend. Assuming direct number or ISO.

                        const chainName = this.dataService.chainId === environment.eth.chainIdInt ? 'ETH' : 'BNB';

                        let eventType: any = 'buy_event';
                        // Determine event type based on purchaseType
                        if (purchaseType === 'claim' || purchaseType === 'claim_failed') {
                            eventType = 'claim_event';
                        } else if (purchaseType === 'stake' || purchaseType === 'unstake' || purchaseType === 'claim_and_stake' || purchaseType === 'harvest') {
                            eventType = 'stake_event';
                        }

                        const eventData: any = {
                            tx_hash: response.transactionHash,
                            block_number: blockNumber,
                            block_timestamp: new Date(blockTimestamp).toISOString()
                        };

                        if (eventType === 'buy_event') {
                            eventData.amount_token_expected = amount;
                            eventData.amount_usd_estimated = purchaseType === 'buy' || purchaseType === 'buy_and_stake' ?
                                (mode === 'usdt' || mode === 'usdc' ? +value : +(+(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2))) : 0;
                            eventData.payment_method = mode.toLowerCase();
                            eventData.type = purchaseType;
                        } else if (eventType === 'claim_event') {
                            eventData.claimable_amount_estimated = this.dataService.balanceDataEVM.totalClaimable;
                        } else if (eventType === 'stake_event') {
                            if (purchaseType === 'stake' || purchaseType === 'claim_and_stake') {
                                eventData.amount_staked = amount;
                            } else if (purchaseType === 'unstake') {
                                eventData.amount_unstaked = this.dataService.userStaked;
                            }
                            eventData.type = purchaseType;
                        }

                        this.trackEvent(eventType, 'success', txId, eventData);

                    } catch {
                        // Fallback to sending event without block details if fetch fails

                        let eventType: any = 'buy_event';
                        // Determine event type based on purchaseType
                        if (purchaseType === 'claim' || purchaseType === 'claim_failed') {
                            eventType = 'claim_event';
                        } else if (purchaseType === 'stake' || purchaseType === 'unstake' || purchaseType === 'claim_and_stake' || purchaseType === 'harvest') {
                            eventType = 'stake_event';
                        }

                        const eventData: any = {
                            tx_hash: response.transactionHash
                        };

                        if (eventType === 'buy_event') {
                            eventData.amount_token_expected = amount;
                            eventData.amount_usd_estimated = purchaseType === 'buy' || purchaseType === 'buy_and_stake' ?
                                (mode === 'usdt' || mode === 'usdc' ? +value : +(+(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2))) : 0;
                            eventData.payment_method = mode.toLowerCase();
                            eventData.type = purchaseType;
                        } else if (eventType === 'claim_event') {
                            eventData.claimable_amount_estimated = this.dataService.balanceDataEVM.totalClaimable;
                        } else if (eventType === 'stake_event') {
                            if (purchaseType === 'stake' || purchaseType === 'claim_and_stake') {
                                eventData.amount_staked = amount;
                            } else if (purchaseType === 'unstake') {
                                eventData.amount_unstaked = this.dataService.userStaked;
                            }
                            eventData.type = purchaseType;
                        }
                        this.trackEvent(eventType, 'success', txId, eventData);

                    }
                }
            },
            (err: any) => {
                this._transactionUpdates.set({ status: SwapStatus.failed })
                this.vwoData('failedAndRejectTransaction', {
                    transactionStatus: 'failed',
                    fundSpentAmount: value,
                    tokenAmountPurchase: amount,
                    purchaseType: purchaseType,
                    tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                    transactionId: hashKey,
                    walletAddress: this.dataService.walletAddress,
                    walletType: this.dataService.connectedWalletName,
                });

                // Track buy failed (transaction revert - only for buy transactions)
                if (purchaseType === 'buy' || purchaseType === 'buy_and_stake') {
                    const chainName = this.dataService.chainId === environment.eth.chainIdInt ? 'ETH' : 'BNB';
                    const usdAmount = mode === 'usdt' || mode === 'usdc' ? +value : +(+(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2));

                    this.trackEvent('buy_event', 'failed', txId, {
                        amount_token_expected: amount,
                        amount_usd_estimated: usdAmount,
                        payment_method: mode.toLowerCase(),
                        tx_hash: hashKey,
                        failure_type: 'revert',
                        failure_message: err?.message || 'Transaction reverted',
                        type: purchaseType
                    });
                }
            },
        );
    }

    /**
     * this function is checking allowence
     * amount before swaping the usdt token with app token
     * @param amount: app token amount
     * @param value: usdt token value
     * @param afterAllowance: checking whether allowence is granted or not
     */

    async swapCryptoForUSDT(amount: number, value: number, afterAllowance: boolean = false, isBuyStake: boolean = false, isUsdc: boolean = false) {
        /**
         * preparing token contract with ABI
         */
        const usdtContract = isUsdc ? getUSDCContract(this.dataService.chainId) : getUSDTContract(this.dataService.chainId);

        // set presale address based on current network
        const presaleAdd = getChainData(this.dataService.chainId).presaleAddress as Address;

        /**
         * reading allowence contract
         */

        const allowanceData = await readContracts(this.dataService.wagmiConfig, {
            contracts: [
                {
                    ...usdtContract,
                    functionName: 'allowance',
                    chainId: this.dataService.chainId,
                    args: [this.dataService.walletAddress, presaleAdd],
                },
            ],
        });


        let decimals;
        let allowanceValue;
        //  checking provided allowance value in token contract
        if (getChainData(this.dataService.chainId) == environment.bsc) {
            allowanceValue = +formatEther(allowanceData[0].result as any);
            decimals = 18;
        } else {
            allowanceValue = Number(allowanceData[0].result) / Math.pow(10, 6);
            decimals = 6;
        }

        /**
         * if inserted usdt value is larger than
         * provided allowence value then it needs further approval.
         * below we are preparing contract for allowence approval
         */

        if (+value > +allowanceValue) {
            const spendingCap = parseUnits(value.toString(), decimals);
            const { request } = await simulateContract(this.dataService.wagmiConfig, {
                ...usdtContract,
                chainId: this.dataService.chainId,
                functionName: 'approve',
                args: [presaleAdd, afterAllowance ? spendingCap : '0'],
                account: this.dataService.walletAddress as Address, // Optional param. Not required!
            });

            // setting swapStatus to approval pending;
            this._transactionUpdates.set({ status: SwapStatus.approval_pending })
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });

            // fetching transaction hash key of writing contract
            const hash = await writeContract(this.dataService.wagmiConfig, request);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });

            /**
             * checking the approval transaction status.
             * if success and the allowence is preapproved
             * then it will call the buyUSDT function, else
             * will again go through the starting steps but this time with
             * allowence approval true mode.
             */
            await waitForTransactionReceipt(this.dataService.wagmiConfig, { hash }).then(
                (success) => {
                    if (afterAllowance) {
                        this.buyUSDT(amount, value, isBuyStake, isUsdc);
                    } else {
                        this.swapCryptoForUSDT(amount, value, true, isBuyStake, isUsdc);
                    }
                },
                (err: any) => {
                    this._transactionUpdates.set({ status: SwapStatus.rejected })
                    this.vwoData('failedAndRejectTransaction', {
                        transactionStatus: 'rejected',
                        fundSpentAmount: value,
                        tokenAmountPurchase: amount,
                        purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                        tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                        transactionId: '',
                        walletAddress: this.dataService.walletAddress,
                        walletType: this.dataService.connectedWalletName,
                    });
                },
            );
        } else {
            /**
             * if inserted usdt value is smaller than
             * provided allowence value, then buyUSDT
             * function will be called and doesn't need
             * to check for allowence approval
             */
            this.buyUSDT(amount, value, isBuyStake, isUsdc);
        }
    }

    /**
     * this function is swaping the usdt token with app token
     * @param amount: app token amount
     * @param value: usdt token value
     * @param afterAllowance: checking whether allowence is granted or not
     */

    async buyUSDT(amount: number, value: number, isBuyStake: boolean = false, isUsdc: boolean = false) {
        /**
         * preparing a contract for swaping via smart contract
         * method of buyWithUSDT
         */

        // const referralAddress = getParam('referralAddress') || "0x0000000000000000000000000000000000000000";
        // const isValidReferral = await checkIsValidReferral(referralAddress, this.dataService.chainId, this.dataService.walletAddress, this.dataService.wagmiConfig);
        // const refferal = isValidReferral ? referralAddress : "0x0000000000000000000000000000000000000000";


        let functionName = isUsdc ? 'buyWithUSDC' : 'buyWithUSDT';
        // Generate unique transaction ID for this buy flow
        const txId = this.pixelService.generateTransactionId();
        const chainName = this.dataService.chainId === environment.eth.chainIdInt ? 'ETH' : 'BNB';
        const paymentMethod = isUsdc ? 'usdc' : 'usdt';

        // Track buy initiated
        this.trackEvent('buy_event', 'initiated', txId, {
            amount_token_expected: amount,
            amount_usd_estimated: value,
            payment_method: paymentMethod,
            type: isBuyStake ? 'buy_and_stake' : 'buy'
        });
        // const referral = await this.getValidReferral();
        const contract = getPresaleContract(this.dataService.chainId);
        await simulateContract(this.dataService.wagmiConfig, {
            ...contract,
            functionName: functionName,
            chainId: this.dataService.chainId,
            args: this.dataService.chainId === environment.eth.chainIdInt ? [amount, isBuyStake] : [amount],
            // args: this.dataService.chainId === environment.eth.chainIdInt ? [amount, false, isBuyStake] : [amount], //for referral
            // account: this.walletAddress as Address   // Optional param. Not required!
        })
            // if prepare contract is success
            .then(
                async (request) => {
                    // setting swapStatus to confirmation pending
                    this._transactionUpdates.set({ status: SwapStatus.confirm_pending })
                    this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                    const isEthChain = this.dataService.chainId === environment.eth.chainIdInt;

                    /**
                     * Once confirmed startig write contract
                     */
                    await writeContract(this.dataService.wagmiConfig, request.request).then(
                        (hash: WriteContractReturnType) => {
                            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                            this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.dataService.walletAddress, hash, this.dataService.balanceDataEVM, this.dataService.chainId, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT); // setting windowdatalayer for analytics;
                            // Track buy pending
                            this.trackEvent('buy_event', 'pending', txId, {
                                amount_token_expected: amount,
                                amount_usd_estimated: value,
                                payment_method: paymentMethod,
                                tx_hash: hash,
                                type: isBuyStake ? 'buy_and_stake' : 'buy'
                            });

                            this._transactionUpdates.set({ status: SwapStatus.in_progess, hash: hash })
                            this.checkTransaction(hash, isUsdc ? 'usdc' : 'usdt', amount, value, isBuyStake ? 'buy_and_stake' : 'buy', isEthChain ? 'eth_usdt' : 'bnb_usdt', txId); // checking transaction status
                        },
                        (error: WriteContractErrorType) => {
                            this._transactionUpdates.set({ status: SwapStatus.rejected })
                            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                            this.vwoData('failedAndRejectTransaction', {
                                transactionStatus: 'rejected',
                                fundSpentAmount: value,
                                tokenAmountPurchase: amount,
                                purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                                tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                                transactionId: '',
                                walletAddress: this.dataService.walletAddress,
                                walletType: this.dataService.connectedWalletName,
                            });
                            // Track buy failed
                            this.trackEvent('buy_event', 'failed', txId, {
                                amount_token_expected: amount,
                                amount_usd_estimated: value,
                                payment_method: paymentMethod,
                                failure_type: 'user_rejected',
                                failure_message: error?.message || 'User rejected transaction',
                                type: isBuyStake ? 'buy_and_stake' : 'buy'
                            });
                        },
                    );
                },

                /**
                 * if there is any error while preparing contract
                 * like requesting more than max-token buy limit
                 * then a warning popup will appear saying that
                 * the max-token limitation
                 */
                (err: any) => {
                    this.popupService.messagePopup({ icon: 'warning', title: this.transletService.instant('widget.max_token_title'), desc: this.transletService.instant('widget.max_token_exceed', { maxTokensToBuy: this.dataService.balanceDataEVM.maxTokensToBuyEVM }) });
                    this._transactionUpdates.set({ status: SwapStatus.rejected })
                    this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                    this.vwoData('failedAndRejectTransaction', {
                        transactionStatus: 'rejected',
                        fundSpentAmount: value,
                        tokenAmountPurchase: amount,
                        purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                        tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                        transactionId: '',
                        walletAddress: this.dataService.walletAddress,
                        walletType: this.dataService.connectedWalletName,
                    });
                    // Track buy failed
                    this.trackEvent('buy_event', 'failed', txId, {
                        amount_token_expected: amount,
                        amount_usd_estimated: value,
                        payment_method: (isUsdc ? 'usdc' : 'usdt').toLowerCase(),
                        type: isBuyStake ? 'buy_and_stake' : 'buy',
                        failure_reason: err?.message || 'Max token limit exceeded'
                    });
                },
            );
    }

    // Withdraw staked tokens
    async withdrawStake() {
        // Generate unique transaction ID for this unstake flow
        const txId = this.pixelService.generateTransactionId();

        // Track unstake initiated
        this.trackEvent('stake_event', 'initiated', txId, {
            amount_unstaked: this.dataService.userStaked,
            type: 'unstake'
        });
        this._transactionUpdates.set({ status: SwapStatus.in_progess })
        const { request } = await simulateContract(this.dataService.wagmiConfig, {
            ...stakeContract,
            functionName: 'withdraw',
            chainId: environment.eth.chainIdInt,
            args: [],
            account: this.dataService.walletAddress as Address, // Optional param. Not required!
        });
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
        await writeContract(this.dataService.wagmiConfig, request).then(
            (hash) => {
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                // Track unstake success
                // Since checkTransaction is not called here, we send success event directly, OR we call checkTransaction.
                // withdrawStake logic sets SwapStatus.complete directly below.
                // Adding success event manually to match other flows.

                this.trackEvent('stake_event', 'success', txId, {
                    amount_unstaked: this.dataService.userStaked,
                    tx_hash: hash,
                    type: 'unstake'
                });

                this._transactionUpdates.set({ status: SwapStatus.complete, hash: hash })
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'completed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                this.vwoData('successfulTransaction', { walletAddress: this.dataService.walletAddress, transactionId: hash, mode: 'CARYPTO', walletName: this.dataService.connectedWalletName, purchaseType: 'withdraw', tokenAmountPurchase: 0, amountSpent: 0, cryptoType: '', usdAmount: 0 });
            },
            (err: any) => {
                this._transactionUpdates.set({ status: SwapStatus.failed })
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });

                // Track unstake failed
                this.trackEvent('stake_event', 'failed', txId, {
                    amount_unstaked: this.dataService.userStaked,
                    failure_type: err.code === 4001 ? 'user_rejected' : 'rpc_error',
                    failure_message: err?.message || 'Unstake failed',
                    type: 'unstake'
                });

                this.vwoData('failedAndRejectTransaction', { transactionStatus: 'failed', fundSpentAmount: 0, tokenAmountPurchase: 0, purchaseType: 'withdrawStake', tokenPurchaseMethod: 'eth', transactionId: '', walletAddress: this.dataService.walletAddress, walletType: this.dataService.connectedWalletName });
            },
        );
    }

    // Claim your staking rewards
    async claimAndStake() {
        // Generate unique transaction ID for this stake flow
        const txId = this.pixelService.generateTransactionId();

        // Track stake initiated
        this.trackEvent('stake_event', 'initiated', txId, {
            amount_staked: this.dataService.balanceDataEVM.ethClaimable,
            type: 'claim_and_stake'
        });

        this._transactionUpdates.set({ status: SwapStatus.in_progess })
        const { request } = await simulateContract(this.dataService.wagmiConfig, {
            ...presaleContractEth,
            functionName: 'claimAndStake',
            chainId: environment.eth.chainIdInt,
            args: [],
            account: this.dataService.walletAddress as Address, // Optional param. Not required!
        });
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
        await writeContract(this.dataService.wagmiConfig, request).then(
            (hash) => {
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                // Track stake pending
                this.trackEvent('stake_event', 'pending', txId, {
                    amount_staked: this.dataService.balanceDataEVM.ethClaimable,
                    tx_hash: hash,
                    type: 'claim_and_stake'
                });

                this.checkTransaction(hash, 'claim_and_stake', 0, 0, 'claim_and_stake', '', txId); // checking transaction status
            },
            (err: any) => {
                this._transactionUpdates.set({ status: SwapStatus.failed })
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                // Track stake failed
                this.trackEvent('stake_event', 'failed', txId, {
                    amount_staked: this.dataService.balanceDataEVM.ethClaimable,
                    failure_type: err.code === 4001 ? 'user_rejected' : 'rpc_error',
                    failure_message: err?.message || 'Stake failed',
                    type: 'claim_and_stake'
                });
                this.vwoData('failedAndRejectTransaction', { transactionStatus: 'failed', fundSpentAmount: 0, tokenAmountPurchase: 0, purchaseType: 'claimAndStake', tokenPurchaseMethod: 'eth', transactionId: '', walletAddress: this.dataService.walletAddress, walletType: this.dataService.connectedWalletName });
            },
        );
    }

    /**
     * Function to claim and stake the token
     */

    async harvestRewards() {
        // Generate unique transaction ID
        const txId = this.pixelService.generateTransactionId();

        // Track initiated
        this.trackEvent('stake_event', 'initiated', txId, {
            type: 'harvest'
        });
        this._transactionUpdates.set({ status: SwapStatus.in_progess })
        const { request } = await simulateContract(this.dataService.wagmiConfig, {
            ...stakeContract,
            functionName: 'harvestRewards',
            chainId: environment.eth.chainIdInt,
            args: [],
            account: this.dataService.walletAddress as Address, // Optional param. Not required!
        });
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
        await writeContract(this.dataService.wagmiConfig, request).then(
            (hash) => {
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                // Track harvest success
                // Since checkTransaction is not called here, we send success event directly, OR we call checkTransaction.
                // withdrawStake logic sets SwapStatus.complete directly below.
                // Adding success event manually to match other flows.
                this.trackEvent('stake_event', 'success', txId, {
                    tx_hash: hash,
                    type: 'harvest'
                });
                this._transactionUpdates.set({ status: SwapStatus.complete, hash: hash })

                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'completed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
            },
            (err: any) => {
                this._transactionUpdates.set({ status: SwapStatus.failed })

                // Track failed
                this.trackEvent('stake_event', 'failed', txId, {
                    failure_type: err.code === 4001 ? 'user_rejected' : 'rpc_error',
                    failure_message: err?.message || 'Harvest failed',
                    type: 'harvest'
                });
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            },
        );
    }

    // Stake your tokens
    async createStake(amount: number) {
        const _wagmiConfig = this.dataService.wagmiConfig;
        const allowanceData = await readContracts(_wagmiConfig, {
            contracts: [
                {
                    ...{
                        abi: token.abi,
                        address: this.dataService.saleToken as Address,
                    },
                    functionName: 'allowance',
                    chainId: this.dataService.chainId,
                    args: [this.dataService.walletAddress, environment.stakingAddress],
                },
            ],
        });
        const allowanceValue = +formatEther(allowanceData[0].result as any);
        const amountEther = +amount;

        if (amountEther > +allowanceValue) {
            const spendingCap = parseUnits(amountEther.toString(), 6);
            const { request } = await simulateContract(_wagmiConfig, {
                ...{
                    abi: token.abi,
                    address: this.dataService.saleToken as Address,
                },
                chainId: this.dataService.chainId,
                functionName: 'approve',
                args: [environment.stakingAddress, spendingCap],
                account: this.dataService.walletAddress as Address, // Optional param. Not required!
            });

            this._transactionUpdates.set({ status: SwapStatus.approval_pending })
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });

            try {
                const hash = await writeContract(_wagmiConfig, request);
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });

                await waitForTransactionReceipt(_wagmiConfig, { hash }).then(
                    (_success) => {
                        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'completed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                        this._transactionUpdates.set({ status: SwapStatus.confirm_pending })
                        this.proceedCreateStake(amount);
                    },
                    (err: any) => {
                        this._transactionUpdates.set({ status: SwapStatus.rejected })
                        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                    },
                );
            } catch (error: any) {
                if (error.message.includes('User rejected')) {
                    this._transactionUpdates.set({ status: SwapStatus.rejected })
                    this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                } else {
                    this._transactionUpdates.set({ status: SwapStatus.failed })
                    this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                    this.vwoData('failedAndRejectTransaction', { transactionStatus: 'failed', fundSpentAmount: 0, tokenAmountPurchase: amount, purchaseType: 'createStake', tokenPurchaseMethod: 'eth', transactionId: '', walletAddress: this.dataService.walletAddress, walletType: this.dataService.connectedWalletName });
                }
            }
        } else {
            this.proceedCreateStake(amount);
        }
    }

    async proceedCreateStake(amount: number) {
        // Generate unique transaction ID for this stake flow
        const txId = this.pixelService.generateTransactionId();

        // Track stake initiated
        this.trackEvent('stake_event', 'initiated', txId, {
            amount_staked: amount,
            type: 'stake'
        });
        const _wagmiConfig = this.dataService.wagmiConfig;
        const { request } = await simulateContract(_wagmiConfig, {
            ...stakeContract,
            functionName: 'deposit',
            chainId: environment.eth.chainIdInt,
            args: [parseEther(amount.toString())],
        });
        this._transactionUpdates.set({ status: SwapStatus.confirm_pending })
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
        await writeContract(_wagmiConfig, request).then(
            (hash: any) => {
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                this._transactionUpdates.set({ status: SwapStatus.in_progess, hash: hash })
                // Track stake pending
                this.trackEvent('stake_event', 'pending', txId, {
                    amount_staked: amount,
                    tx_hash: hash,
                    type: 'stake'
                });
                this.checkTransaction(hash, '', amount, 0, 'stake', '', txId);
            },
            (err: any) => {
                this._transactionUpdates.set({ status: SwapStatus.rejected })
                // Track stake failed
                this.trackEvent('stake_event', 'failed', txId, {
                    amount_staked: amount,
                    failure_type: err.code === 4001 ? 'user_rejected' : 'rpc_error',
                    failure_message: err?.message || 'Stake failed',
                    type: 'stake'
                });
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            },
        );
    }

    vwoData(eventName: string, eventParams: any, hasParam = true) {
        this.analyticsService.vwoData(eventName, eventParams, hasParam);
    }

    /**
     * Function to buy app token via wert widget.
     * this function convert the USDT value to app token
     * @param amount: Amount to spend
     * @returns : converted no of app tokens
     */

    // getDynamicAmountWertEth = async (amount: number) => {
    //     console.log('getDynamicAmountWert', amount);
    //     const _wagmiConfig = this.dataService.wagmiConfig;
    //     const contractData = await readContracts(_wagmiConfig, {
    //         contracts: [
    //             {
    //                 ...presaleContractEth,
    //                 functionName: 'ethBuyHelper',
    //                 chainId: environment.eth.chainIdInt,
    //                 args: [amount],
    //             },
    //         ],
    //     });
    //     let ethVal = +formatEther(contractData[0].result as any);
    //     console.log('return DynamicAmountWert', ethVal);
    //     return +ethVal;
    // };

}
